// Static data-integrity + internal-link checks.
// Pure functions so they can run in a Node/Bun script (CI-style) and inside
// the /data-integrity page without duplicating logic.

import { PALS } from "@/lib/pals-data";
import { GUIDES } from "@/lib/guides-data";
import { OFFICIAL_CARDS } from "@/lib/tcg-cards";

export type Severity = "error" | "warn";

export interface IntegrityIssue {
  check: string;
  severity: Severity;
  message: string;
}

export interface IntegrityCheck {
  name: string;
  passed: boolean;
  count: number;
  issues: IntegrityIssue[];
}

/** Every static (non-parameterised) route the app serves. */
export const STATIC_ROUTES = [
  "/", "/about", "/account", "/auth", "/bosses", "/boss-route", "/breeding", "/capture-rates", "/card-checker",
  "/cards", "/contact", "/damage", "/deck-builder", "/eidrolon", "/elements",
  "/favorites", "/guides", "/link-audit", "/data-integrity", "/map", "/mounts", "/pals",
  "/privacy", "/security-disclaimer", "/tcg-tools", "/technology-tree", "/terms", "/tier-list",
  "/sitemap.xml", "/sitemap-images.xml", "/robots.txt", "/llms.txt",
];


/** Resolve whether an internal path maps to a real route. */
export function isValidInternalPath(rawPath: string): boolean {
  const path = rawPath.split("#")[0].split("?")[0].replace(/\/$/, "") || "/";
  if (STATIC_ROUTES.includes(path)) return true;

  const palMatch = /^\/pals\/([a-z0-9-]+)$/.exec(path);
  if (palMatch) return PALS.some((p) => p.slug === palMatch[1]);

  const guideMatch = /^\/guides\/([a-z0-9-]+)$/.exec(path);
  if (guideMatch) return GUIDES.some((g) => g.slug === guideMatch[1]);

  const articleMatch = /^\/guides\/([a-z0-9-]+)\/([a-z0-9-]+)$/.exec(path);
  if (articleMatch) {
    const g = GUIDES.find((x) => x.slug === articleMatch[1]);
    return !!g && g.articles.some((a) => a.slug === articleMatch[2]);
  }
  return false;
}

/** First segments that unambiguously identify a site route (vs. prose like "8/10"). */
const ROUTE_SEGMENTS = new Set(
  STATIC_ROUTES.map((r) => r.split("/")[1]).filter(Boolean)
);

/** True when the text looks like a site link attempt rather than prose/units/file paths. */
export function looksLikeSitePath(path: string): boolean {
  const seg = path.split("/")[1]?.split(/[?#]/)[0] ?? "";
  return ROUTE_SEGMENTS.has(seg.toLowerCase()) && seg === seg.toLowerCase();
}

/** Pull every internal path referenced from guide article copy + intros. */
export function collectGuideInternalLinks(): { path: string; where: string }[] {
  const out: { path: string; where: string }[] = [];
  const re = /(\/[a-z0-9][a-z0-9\-/]*)/gi;
  for (const g of GUIDES) {
    const push = (text: string, where: string) => {
      let m: RegExpExecArray | null;
      re.lastIndex = 0;
      while ((m = re.exec(text)) !== null) {
        const p = m[1].replace(/[.,;:!?)]+$/, "");
        if (p.length > 1 && looksLikeSitePath(p)) out.push({ path: p, where });
      }
    };
    push(g.desc, `guide:${g.slug} desc`);
    for (const a of g.articles) {
      push(a.intro, `guide:${g.slug}/${a.slug} intro`);
      for (const s of a.sections) push(s.text, `guide:${g.slug}/${a.slug} § ${s.heading}`);
    }
  }
  return out;
}

function dupes<T>(items: T[]): T[] {
  const seen = new Set<T>();
  const dup = new Set<T>();
  for (const i of items) (seen.has(i) ? dup : seen).add(i);
  return [...dup];
}

export function runIntegrityChecks(): IntegrityCheck[] {
  const checks: IntegrityCheck[] = [];

  // --- Pals ---------------------------------------------------------------
  const palIssues: IntegrityIssue[] = [];
  for (const s of dupes(PALS.map((p) => p.slug)))
    palIssues.push({ check: "pals", severity: "error", message: `Duplicate Pal slug: ${s}` });
  for (const id of dupes(PALS.map((p) => p.id)))
    palIssues.push({ check: "pals", severity: "error", message: `Duplicate Pal id: ${id}` });
  for (const p of PALS) {
    if (!p.name?.trim()) palIssues.push({ check: "pals", severity: "error", message: `Pal ${p.slug} has no name` });
    if (!p.element) palIssues.push({ check: "pals", severity: "error", message: `${p.name} missing element` });
    if (!p.moves?.length) palIssues.push({ check: "pals", severity: "warn", message: `${p.name} has no moves` });
    if (!(p.hp > 0)) palIssues.push({ check: "pals", severity: "error", message: `${p.name} has invalid HP` });
  }
  checks.push({
    name: `Pal records (${PALS.length})`,
    count: PALS.length,
    issues: palIssues,
    passed: !palIssues.some((i) => i.severity === "error"),
  });

  // --- Guides -------------------------------------------------------------
  const guideIssues: IntegrityIssue[] = [];
  for (const s of dupes(GUIDES.map((g) => g.slug)))
    guideIssues.push({ check: "guides", severity: "error", message: `Duplicate guide slug: ${s}` });
  for (const g of GUIDES) {
    if (g.articles.length !== 5)
      guideIssues.push({ check: "guides", severity: "warn", message: `${g.slug} has ${g.articles.length} articles (rule: 5)` });
    for (const s of dupes(g.articles.map((a) => a.slug)))
      guideIssues.push({ check: "guides", severity: "error", message: `${g.slug} duplicate article slug: ${s}` });
    for (const a of g.articles) {
      if (!a.sections.length)
        guideIssues.push({ check: "guides", severity: "error", message: `${g.slug}/${a.slug} has no sections` });
      if ((a.externalLinks?.length ?? 0) < 3)
        guideIssues.push({ check: "guides", severity: "warn", message: `${g.slug}/${a.slug} has <3 external links` });
      if (!a.intro || a.intro.length < 40)
        guideIssues.push({ check: "guides", severity: "warn", message: `${g.slug}/${a.slug} intro too short for AEO` });
    }
  }
  checks.push({
    name: `Guides (${GUIDES.length})`,
    count: GUIDES.length,
    issues: guideIssues,
    passed: !guideIssues.some((i) => i.severity === "error"),
  });

  // --- TCG cards ----------------------------------------------------------
  const cardIssues: IntegrityIssue[] = [];
  for (const c of dupes(OFFICIAL_CARDS.map((c) => c.code.toUpperCase())))
    cardIssues.push({ check: "cards", severity: "error", message: `Duplicate card code: ${c}` });
  for (const c of OFFICIAL_CARDS) {
    // Play accessories (life/material counters) aren't printed with an E set code.
    const isAccessory = /-COUNTER$/.test(c.code.toUpperCase());
    if (!isAccessory && !/^E[A-Z]+\d*-\d+/.test(c.code.toUpperCase()))
      cardIssues.push({ check: "cards", severity: "error", message: `Card code not in printed E-format: ${c.code}` });
    if (!c.name?.trim())
      cardIssues.push({ check: "cards", severity: "error", message: `Card ${c.code} missing name` });
    if (!c.rarity)
      cardIssues.push({ check: "cards", severity: "warn", message: `Card ${c.code} missing rarity` });
  }
  checks.push({
    name: `Official TCG cards (${OFFICIAL_CARDS.length})`,
    count: OFFICIAL_CARDS.length,
    issues: cardIssues,
    passed: !cardIssues.some((i) => i.severity === "error"),
  });

  // --- Internal links in guide copy --------------------------------------
  const linkIssues: IntegrityIssue[] = [];
  const links = collectGuideInternalLinks();
  const bad = new Map<string, string[]>();
  for (const l of links) {
    if (!isValidInternalPath(l.path)) {
      const arr = bad.get(l.path) ?? [];
      arr.push(l.where);
      bad.set(l.path, arr);
    }
  }
  for (const [path, wheres] of bad)
    linkIssues.push({
      check: "links",
      severity: "error",
      message: `Broken internal link ${path} (${wheres.length}× — e.g. ${wheres[0]})`,
    });
  checks.push({
    name: `Internal guide links (${links.length} refs)`,
    count: links.length,
    issues: linkIssues,
    passed: linkIssues.length === 0,
  });

  return checks;
}
